import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Check,
  Loader,
  Loader2,
  MoveRight,
  Plus,
  RefreshCcw,
  Save,
} from "lucide-react";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordian";

import { CAR_TYPES } from "../../constants/cars";
import { MODES } from "../../constants/booking-modes";
import {
  addLongDistanceRecord,
  changeLongDistanceRecord,
  changeZoneStatus,
  removeLongDistanceRecord,
  updateInfinityRecordPrice,
} from "../../redux/pricing/slice";
import { Badge } from "../ui/badge";

export function TableLongDistancePricing() {
  const dispatch = useDispatch();

  const [value, setValue] = React.useState(undefined);

  const authToken = useSelector(({ auth }) => auth.token);

  const zones = useSelector(({ pricing }) => pricing.zones);
  const zoneStatus = useSelector(({ pricing }) => pricing.zoneStatus);

  const currentSelectedCityId = useSelector(
    ({ pricing }) => pricing.currentSelectedCityId
  );

  const currency = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId].currency
  );

  const longDistanceZoneIds = useSelector(({ pricing }) =>
    Object.keys(pricing.zones).filter(
      (zoneId) =>
        pricing.zones[zoneId].bookingType === MODES[2] &&
        currentSelectedCityId === pricing.zones[zoneId].cityId
    )
  );

  const longDistanceZoneId = longDistanceZoneIds[0];

  const currentSelectedCity = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId]
  );

  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const handleCreateLongDistanceZone = async () => {
    dispatch({
      type: "EXTRANET_CREATE_ZONE_BY_CITY_ID",
      payload: {
        bookingType: MODES[2],
        currency,
        cityId: currentSelectedCityId,
        pricing: {},
        token: authToken,
        callbacks: { setIsLoading, setIsError },
      },
    });
  };

  const isRangeValid = (pricingList) => {
    if (!pricingList || pricingList.length === 0) return true;

    let lastEnd = -1;

    for (let i = 0; i < pricingList.length; i++) {
      const { start, end } = pricingList[i];

      if (end === "INF") {
        if (i !== pricingList.length - 1) return false; // INF must be last
      }

      if (start !== lastEnd + 1) return false;
      if (start > end) return false;
      lastEnd = end;
    }

    return true;
  };

  const handleSaveZonePricing = async (zoneId) => {
    if (
      Object.keys(zones[zoneId].pricing)
        .map((CAR) => isRangeValid(zones?.[zoneId]?.pricing?.[CAR] || []))
        .every((val) => val)
    ) {
      dispatch({
        type: "EXTRANET_UPDATE_ZONE_PRICNG_BY_ID",
        payload: {
          zoneId,
        },
      });
    }
  };

  return (
    <>
      {longDistanceZoneIds.length === 0 && (
        <div className="pt-24 text-center">
          <Button disabled={isLoading} onClick={handleCreateLongDistanceZone}>
            {!isError && !isLoading && <Plus />}
            {isLoading && <Loader2 className="animate-spin" />}
            {isError && <RefreshCcw className="text-red-500" />}
            <span>Set Long Distance Price</span>
          </Button>
        </div>
      )}

      {longDistanceZoneIds.length !== 0 && (
        <Accordion value={value} className="space-y-2 text-white">
          {CAR_TYPES.map((CAR) => (
            <AccordionItem
              value={CAR.id}
              key={CAR.id}
              className="hover:cursor-pointer px-2 py-4 pb-8 border border-[#353c4a] rounded-md p-2 md:p-4"
            >
              <AccordionTrigger
                className="hover:no-underline"
                onClick={() => {
                  if (CAR.id === value) setValue(null);
                  else setValue(CAR.id);
                }}
              >
                <div>
                  {CAR.categoryType}{" "}
                  {!zoneStatus[longDistanceZoneId].isChanged &&
                    zones[longDistanceZoneId].pricing[CAR.categoryType] &&
                    zones[longDistanceZoneId].pricing[CAR.categoryType].length >
                      0 && (
                      <Badge className={"text-green-300"}>Pricing given</Badge>
                    )}
                  {(!zones[longDistanceZoneId].pricing[CAR.categoryType] ||
                    zones[longDistanceZoneId].pricing[CAR.categoryType]
                      .length == 0) && (
                    <Badge className={"text-red-400"}>Pricing not given</Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="relative min-w-80 min-h-80 flex-1 overflow-x-scroll scrollbar-none">
                {/* Table Heading */}
                <div className="flex items-center gap-1 sm:gap-2 md:gap-4 mb-4">
                  <div
                    placeholder="Enter"
                    className="max-w-36 font-medium px-2 text-white w-full truncate "
                  >
                    Start ({currentSelectedCity.distanceUnit}){" "}
                  </div>
                  <MoveRight className="size-4 shrink-0 text-transparent" />
                  <div
                    placeholder="Enter"
                    className="max-w-36 font-medium px-2 text-white w-full truncate "
                  >
                    End ({currentSelectedCity.distanceUnit}){" "}
                  </div>
                  <div
                    className="h-6 w-px bg-transparent"
                    aria-hidden="true"
                  ></div>
                  <div
                    placeholder="Enter"
                    className="max-w-36 font-medium px-2 text-white w-full truncate "
                  >
                    Price ({currency})
                  </div>
                </div>

                {/* Table rows */}
                {longDistanceZoneId &&
                  Object.keys(zones[longDistanceZoneId].pricing).length > 0 && (
                    <>
                      {zones[longDistanceZoneId].pricing[CAR.categoryType] &&
                        zones[longDistanceZoneId].pricing[CAR.categoryType].map(
                          (_, index) => {
                            if (
                              zones[longDistanceZoneId].pricing[
                                CAR.categoryType
                              ][index].end === "INF"
                            )
                              return null;

                            return (
                              <div
                                className="flex items-center gap-1 sm:gap-2 md:gap-4 mb-4"
                                key={`${CAR.categoryType}-${index}`}
                              >
                                <Input
                                  disabled={index === 0}
                                  value={
                                    zones[longDistanceZoneId].pricing[
                                      CAR.categoryType
                                    ][index].start
                                  }
                                  placeholder="Enter Placeholder"
                                  className="max-w-36 focus-visible:ring-offset-0 bg-[#131821] border border-[#353C4A] block w-full truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-2 text-white"
                                  onChange={(e) => {
                                    if (!isNaN(Number(e.target.value))) {
                                      dispatch(
                                        changeLongDistanceRecord({
                                          zoneId: longDistanceZoneId,
                                          carName: CAR.categoryType,
                                          index,
                                          key: "start",
                                          value: Number(e.target.value),
                                        })
                                      );
                                      dispatch(
                                        changeZoneStatus({
                                          zoneId: longDistanceZoneId,
                                          newStatus: {
                                            isChanged: true,
                                          },
                                        })
                                      );
                                    }
                                  }}
                                />
                                <MoveRight className="size-4 shrink-0" />
                                <Input
                                  placeholder="Enter Placeholder"
                                  className="max-w-36 focus-visible:ring-offset-0 bg-[#131821] border border-[#353C4A] block w-full truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-2 text-white"
                                  value={
                                    zones[longDistanceZoneId].pricing[
                                      CAR.categoryType
                                    ][index].end
                                  }
                                  onChange={(e) => {
                                    if (!isNaN(Number(e.target.value))) {
                                      dispatch(
                                        changeLongDistanceRecord({
                                          zoneId: longDistanceZoneId,
                                          carName: CAR.categoryType,
                                          index,
                                          key: "end",
                                          value: Number(e.target.value),
                                        })
                                      );
                                      dispatch(
                                        changeZoneStatus({
                                          zoneId: longDistanceZoneId,
                                          newStatus: {
                                            isChanged: true,
                                          },
                                        })
                                      );
                                    }
                                  }}
                                />
                                <div className="h-6 w-px bg-gray-500"></div>
                                <Input
                                  placeholder="Enter Placeholder"
                                  className="max-w-36 focus-visible:ring-offset-0 bg-[#131821] border border-[#353C4A] block w-full truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-2 text-white"
                                  value={
                                    zones[longDistanceZoneId].pricing[
                                      CAR.categoryType
                                    ][index].price
                                  }
                                  onChange={(e) => {
                                    if (!isNaN(Number(e.target.value))) {
                                      dispatch(
                                        changeLongDistanceRecord({
                                          zoneId: longDistanceZoneId,
                                          carName: CAR.categoryType,
                                          index,
                                          key: "price",
                                          value: Number(e.target.value),
                                        })
                                      );
                                      dispatch(
                                        changeZoneStatus({
                                          zoneId: longDistanceZoneId,
                                          newStatus: {
                                            isChanged: true,
                                          },
                                        })
                                      );
                                    }
                                  }}
                                />
                                <Button
                                  className="hover:bg-red-400"
                                  disabled={
                                    zones[longDistanceZoneId].pricing[
                                      CAR.categoryType
                                    ].length < 3
                                  }
                                  onClick={() => {
                                    dispatch(
                                      removeLongDistanceRecord({
                                        zoneId: longDistanceZoneId,
                                        carName: CAR.categoryType,
                                        index,
                                      })
                                    );
                                    dispatch(
                                      changeZoneStatus({
                                        zoneId: longDistanceZoneId,
                                        newStatus: {
                                          isChanged: true,
                                        },
                                      })
                                    );
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            );
                          }
                        )}

                      {zones[longDistanceZoneId].pricing[CAR.categoryType] &&
                        zones[longDistanceZoneId].pricing[CAR.categoryType]
                          .length > 1 && (
                          <div className="flex items-center gap-1 sm:gap-2 md:gap-4 mb-4">
                            <Input
                              value={
                                zones[longDistanceZoneId].pricing[
                                  CAR.categoryType
                                ]?.find((r) => r.end === "INF")?.start ?? ""
                              }
                              disabled
                              placeholder="INF Start"
                              className="max-w-36 flex-1 focus-visible:ring-offset-0 bg-[#131821] border border-[#353C4A] block truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-2 text-white"
                            />
                            <MoveRight className="size-4 shrink-0" />
                            <Input
                              disabled
                              value="Above"
                              placeholder="INF Start"
                              className="max-w-36 flex-1 focus-visible:ring-offset-0 bg-[#131821] border border-[#353C4A] block w-full truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-2 text-white"
                            />
                            <div className="h-6 w-px bg-gray-500"></div>
                            <Input
                              placeholder="Enter Placeholder"
                              // className="max-w-36 focus-visible:ring-offset-0 bg-[#131821] border border-[#353C4A] block truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-2 text-white"
                              className="max-w-36 flex-1 focus-visible:ring-offset-0 bg-[#131821] border border-[#353C4A] block w-full truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-2 text-white"
                              value={
                                zones[longDistanceZoneId].pricing[
                                  CAR.categoryType
                                ]?.find((r) => r.end === "INF")?.price || ""
                              }
                              onChange={(e) => {
                                if (!isNaN(Number(e.target.value))) {
                                  dispatch(
                                    updateInfinityRecordPrice({
                                      zoneId: longDistanceZoneId,
                                      carName: CAR.categoryType,
                                      price: Number(e.target.value),
                                    })
                                  );
                                  dispatch(
                                    changeZoneStatus({
                                      zoneId: longDistanceZoneId,
                                      newStatus: {
                                        isChanged: true,
                                      },
                                    })
                                  );
                                }
                              }}
                            />

                            <Button
                              className="px-6 bg-gray-900 hover:bg-gray-900 hover:cursor-not-allowed"
                              onClick={() => {}}
                            >
                              Last
                            </Button>
                          </div>
                        )}

                      {zones[longDistanceZoneId].pricing[CAR.categoryType] &&
                        zones[longDistanceZoneId].pricing[CAR.categoryType]
                          .length > 1 &&
                        !isRangeValid(
                          zones?.[longDistanceZoneId]?.pricing?.[
                            CAR.categoryType
                          ] || []
                        ) && (
                          <p className="text-red-300 text-sm mb-4">
                            Given {currentSelectedCity.distanceUnit} Range for "
                            {CAR.categoryType}" is not continuous.
                          </p>
                        )}

                      <div className="h-px w-full mb-4 bg-gray-500"></div>
                    </>
                  )}

                <div className="flex w-full flex-wrap gap-2">
                  <Button
                    className="min-w-52 text-xs"
                    onClick={() => {
                      dispatch(
                        addLongDistanceRecord({
                          zoneId: longDistanceZoneId,
                          carName: CAR.categoryType,
                        })
                      );
                      dispatch(
                        changeZoneStatus({
                          zoneId: longDistanceZoneId,
                          newStatus: {
                            isChanged: true,
                          },
                        })
                      );
                    }}
                  >
                    <Plus />
                    Add a Row
                  </Button>

                  {zoneStatus[longDistanceZoneId].isChanged && (
                    <Button
                      className="min-w-52"
                      onClick={() => handleSaveZonePricing(longDistanceZoneId)}
                    >
                      {zoneStatus[longDistanceZoneId].isChanged &&
                        !zoneStatus[longDistanceZoneId].isLoading && <Save />}
                      {zoneStatus[longDistanceZoneId].isLoading && (
                        <Loader2 className="animate-spin" />
                      )}
                      Save Pricings
                    </Button>
                  )}

                  {!zoneStatus[longDistanceZoneId].isChanged &&
                    zones[longDistanceZoneId].pricing[CAR.categoryType] && (
                      <Button
                        variant={"outline"}
                        type="button"
                        className="bg-transparent hover:bg-transparent border-[#353c4a]"
                      >
                        <Check className="text-green-500" />
                        <span className="text-white ">Up to date</span>
                      </Button>
                    )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </>
  );
}
